import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import {LinkedEntity} from '../linked-entity/linked-entity';
import type {Participant} from '../timeline/types';
import {shouldShowDelimiter} from '../utils/should-show-delimiter';

import styles from './truncated-group.module.css';

type Props = {
    participants: Participant[];
    includeMoreWord?: boolean;
    maxWidth: number;
    showEmailAddressTooltip?: boolean;
    onClickParticipant?: (participant: Participant, element: HTMLElement) => void;
};

// We need a sane cap so that React doesn't continually try and re-render
// hundreds of participants 1 by 1, potentially causing it to think its
// in an infinite render cycle. Some activities have hundreds of people
const MAX_PARTICIPANTS_SHOWN_EVER = 10;

// This component is used when we want to display a limited group, but instead of
// passing in the number of items to display, we pass in a maxWidth and then
// calculate how many items can be displayed.
export function TruncatedGroup(props: Props) {
    const {participants, includeMoreWord, maxWidth, showEmailAddressTooltip, onClickParticipant} =
        props;

    const models = React.useMemo(() => {
        return participants.slice(0, MAX_PARTICIPANTS_SHOWN_EVER).map((participant, index) => {
            const handleParticipantClick = (e: Event) => {
                if (onClickParticipant && e.currentTarget instanceof HTMLElement) {
                    onClickParticipant(participant, e.currentTarget);
                }
            };

            return (
                <span
                    key={participant.id}
                    style={{display: index !== 0 ? 'inline-flex' : ''}}
                    data-tip={
                        showEmailAddressTooltip && participant.emailAddress
                            ? participant.emailAddress
                            : undefined
                    }
                    data-place='bottom'
                >
                    <LinkedEntity
                        useEntityColor={true}
                        stopPropagation={true}
                        shouldClientRoute={true}
                        greenLeads={true}
                        htmlUrl={participant.htmlUrl}
                        type={participant.type}
                        name={participant.name}
                        allowWrap={index === 0}
                        displayInline={index === 0}
                        email={participant.email}
                        phone={participant.phone}
                        onCustomClick={onClickParticipant ? handleParticipantClick : undefined}
                    />
                    {shouldShowDelimiter(index, participants.length) ? (
                        <span>,&nbsp;</span>
                    ) : undefined}
                </span>
            );
        });
    }, [participants, showEmailAddressTooltip, onClickParticipant]);

    const participantNames: string[] = participants.map((participant) => participant.name);

    const [numDisplayed, setNumDisplayed] = React.useState<number>(models.length);

    const firstModelRef = React.useRef(null);
    const shownModelsRef = React.useRef(null);

    // Reset numDisplayed when maxWidth changes so the following useLayoutEffect
    // can properly calculate the width of all participants
    React.useLayoutEffect(() => {
        if (maxWidth > 0) {
            setNumDisplayed(Math.min(participants.length, MAX_PARTICIPANTS_SHOWN_EVER));
        }
    }, [participants, maxWidth]);

    // Decrease the number displayed until the width of the div holding the
    // displayed models can fit inside the maxWidth, always displaying at least 1
    React.useLayoutEffect(() => {
        if (maxWidth > 0) {
            const shownContainer = shownModelsRef.current;
            const firstModel = firstModelRef.current;

            if (shownContainer && firstModel) {
                // @ts-expect-error
                const currentWidth = shownContainer.clientWidth + firstModel.clientWidth;

                if (currentWidth > maxWidth && numDisplayed > 1) {
                    setNumDisplayed(Math.min(numDisplayed - 1, MAX_PARTICIPANTS_SHOWN_EVER));
                }
            }
        }
    }, [maxWidth, participants, numDisplayed]);

    const shownModels = models.slice(0, numDisplayed);
    const firstModel = shownModels.shift(); // We will always show the first model
    const hiddenNames: string[] = participantNames.slice(numDisplayed);

    React.useEffect(() => {
        ReactTooltip.rebuild();

        return () => ReactTooltip.hide();
    }, [hiddenNames]);

    return (
        <span>
            <span
                // If this is not the only model displayed, this needs to be inline-block
                className={shownModels.length ? 'inline-block' : ''}
                ref={(ref) => {
                    if (ref) {
                        // @ts-expect-error
                        firstModelRef.current = ref;
                    }
                }}
            >
                {firstModel}
            </span>
            {shownModels.length || hiddenNames.length ? (
                <div className={styles.container}>
                    <div ref={shownModelsRef} className={styles.shown}>
                        {shownModels}
                    </div>
                    {hiddenNames.length ? (
                        <div
                            data-tip={hiddenNames.join('<br />')}
                            data-place='bottom'
                            className={styles.hidden}
                        >
                            +{hiddenNames.length}
                            {includeMoreWord ? ' more' : ''}
                        </div>
                    ) : undefined}
                </div>
            ) : undefined}
        </span>
    );
}
